import React, { useState, useEffect } from "react";
import axiosInstance from "../api/axiosInstance";
import {
  Autocomplete,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
} from "@mui/material";
import { createFilterOptions } from "@mui/material/Autocomplete";

const baseFilter = createFilterOptions({
  stringify: (o) =>
    `${o.customercontactfirstname || ""} ${o.customercontactlastname || ""}`,
});

const EMPTY_CONTACT = {
  name: "",
  mobile: "",
  email: "",
  address: "",
  area_location: "",
  city: "",
  postal_code: "",
  title: "",
  department: "",
  designation: "",
};

export default function ContactAutocompleteWithAddNew({
  label,
  contacts,
  formValue,
  onFormChange,
  onValueChange = () => {},
  clientId,
  customerId,
  form,
  onContactAdded,
  fieldPrefix,
  contactType, // "Orderby" | "Usedby"
}) {
  const [openDialog, setOpenDialog] = useState(false);
  const [newContact, setNewContact] = useState(EMPTY_CONTACT);
  const [options, setOptions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    setOptions(Array.isArray(contacts) ? contacts : []);
  }, [contacts]);

  // Single source of truth for the field-name prefix (order_by / used_by ...)
  const prefix =
    fieldPrefix || (label || "").toLowerCase().replace(/\s/g, "_");

  const resolvedContactType =
    contactType ||
    (prefix === "order_by" ? "Orderby" : "Usedby");

  const addNewOption = { isAddNew: true, label: `+ Add New ${label || ""}` };

  const realOptions = options.filter((opt) => !opt.isAddNew);

  const setField = (field, value) =>
    onFormChange({ target: { name: `${prefix}_${field}`, value } });

  const handleAddNewClick = () => {
    setNewContact(EMPTY_CONTACT);
    setSaveError(null);
    setOpenDialog(true);
  };

  const clearFields = () => {
    ["name", "mobile", "email", "department", "designation"].forEach((f) =>
      setField(f, ""),
    );
  };

  const handleDialogSubmit = async () => {
    const trimmedName = newContact.name.trim();
    if (!trimmedName) return;

    try {
      setSaving(true);
      setSaveError(null);

      const names = trimmedName.split(" ");

      const payload = {
        client_id: clientId,
        customerid: customerId,
        customercontactsalutation:
          (form && form[`${prefix}_title`]) || newContact.title || "",
        customercontactfirstname: names[0] || "",
        customercontactlastname: names.slice(1).join(" ") || "",
        customercontactmobile: newContact.mobile || "",
        customercontactemail: newContact.email || "",
        customercontactdesignation: newContact.designation || "",
        customercontactdepartment: newContact.department || "",
        customercontactcontacttype: resolvedContactType,
        customercontactprimary: false,
        address: newContact.address || "",
        area_location: newContact.area_location || "",
        city: newContact.city || "",
        postal_code: newContact.postal_code || "",
      };

      const response = await axiosInstance.post(
        `/api/customercontacts/customer/${customerId}`,
        payload,
      );

      const savedContact = {
        contactid: response.data.contactid,
        client_id: payload.client_id,
        customerid: payload.customerid,
        customercontactsalutation: payload.customercontactsalutation,
        customercontactfirstname: payload.customercontactfirstname,
        customercontactlastname: payload.customercontactlastname,
        customercontactmobile: payload.customercontactmobile,
        customercontactemail: payload.customercontactemail,
        customercontactdesignation: payload.customercontactdesignation,
        customercontactdepartment: payload.customercontactdepartment,
        customercontactcontacttype: payload.customercontactcontacttype,
        customercontactprimary: payload.customercontactprimary,
        address: payload.address,
        area_location: payload.area_location,
        city: payload.city,
        postal_code: payload.postal_code,
      };

      if (onContactAdded) onContactAdded(savedContact);

      const savedContactName =
        `${savedContact.customercontactfirstname} ${savedContact.customercontactlastname}`.trim();

      setOptions((prev) => [
        ...prev.filter((opt) => !opt.isAddNew),
        savedContact,
      ]);

      // Update form fields (using the correct prefix)
      setField("name", savedContactName);
      setField("mobile", newContact.mobile);
      setField("email", newContact.email);
      setField("department", newContact.department);
      setField("designation", newContact.designation);

      onValueChange(null, savedContact);

      setOpenDialog(false);
    } catch (error) {
      setSaveError("Failed to save contact. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field) => (event) => {
    setNewContact((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  return (
    <>
      <Autocomplete
        freeSolo={false}
        options={[...realOptions, addNewOption]}
        // Always keep the "+ Add New" option visible, even while typing
        filterOptions={(opts, state) => [
          ...baseFilter(
            opts.filter((o) => !o.isAddNew),
            state,
          ),
          addNewOption,
        ]}
        isOptionEqualToValue={(opt, val) => opt === val}
        getOptionLabel={(option) =>
          option.isAddNew
            ? option.label
            : `${option.customercontactfirstname || ""} ${option.customercontactlastname || ""}`.trim()
        }
        value={null} // we don't bind full object, just inputValue
        inputValue={formValue || ""} // always show form's name string
        onChange={(event, newValue) => {
          if (newValue?.isAddNew) {
            handleAddNewClick();
            return;
          }

          if (newValue) {
            const fullName = `${newValue.customercontactfirstname || ""} ${
              newValue.customercontactlastname || ""
            }`.trim();

            setField("name", fullName);
            setField("mobile", newValue.customercontactmobile || "");
            setField("email", newValue.customercontactemail || "");
            setField("department", newValue.customercontactdepartment || "");
            setField("designation", newValue.customercontactdesignation || "");
          } else {
            clearFields();
          }

          // Notify the parent for both selection and clearing.
          onValueChange(event, newValue);
        }}
        onInputChange={(event, newInputValue, reason) => {
          if (reason === "input") {
            // user is typing -> always editable, uses the correct prefix
            setField("name", newInputValue);
          } else if (reason === "clear") {
            // value is always null so MUI does not fire onChange on clear
            clearFields();
            onValueChange(event, null);
          }
        }}
        renderInput={(params) => {
          const safeLabel = label || "";
          return (
            <TextField
              {...params}
              label={safeLabel}
              size="small"
              autoComplete="new-password"
              name={`${prefix}_name`}
              spellCheck={false}
            />
          );
        }}
        renderOption={(props, option) =>
          option.isAddNew ? (
            <li
              {...props}
              key="add-new"
              style={{ fontWeight: "bold", color: "blue", cursor: "pointer" }}
            >
              {option.label}
            </li>
          ) : (
            <li
              {...props}
              style={{
                whiteSpace: "normal",
                paddingBottom: 8,
                display: "block",
              }}
            >
              <div
                style={{
                  fontWeight: "normal",
                  fontSize: "1rem",
                  lineHeight: "1.4",
                }}
              >
                {option.customercontactfirstname}{" "}
                {option.customercontactlastname}
              </div>
              {[
                option.address,
                option.area_location,
                option.city,
                option.postal_code,
              ].filter(Boolean).length > 0 && (
                <div
                  style={{
                    fontSize: "0.70em",
                    color: "#666",
                    marginTop: 0,
                    lineHeight: "1.2",
                  }}
                >
                  {[
                    option.address,
                    option.area_location,
                    option.city,
                    option.postal_code,
                  ]
                    .filter(Boolean)
                    .join(", ")}
                </div>
              )}
              {[
                option.customercontactdepartment,
                option.customercontactdesignation,
              ].filter(Boolean).length > 0 && (
                <div
                  style={{
                    fontSize: "0.7em",
                    fontWeight: "bold",
                    color: "#888",
                    fontStyle: "italic",
                    marginTop: 0,
                    lineHeight: "1.2",
                  }}
                >
                  {[
                    option.customercontactdepartment,
                    option.customercontactdesignation,
                  ]
                    .filter(Boolean)
                    .join(", ")}
                </div>
              )}
            </li>
          )
        }
      />

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Add New {label}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1, mb: 1 }}>
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel id="title-label">Title</InputLabel>
              <Select
                labelId="title-label"
                label="Title"
                value={newContact.title}
                onChange={handleChange("title")}
                disabled={saving}
              >
                <MenuItem value="">
                  <em>Select Title</em>
                </MenuItem>
                <MenuItem value="Mr.">Mr.</MenuItem>
                <MenuItem value="Ms.">Ms.</MenuItem>
                <MenuItem value="Mrs.">Mrs.</MenuItem>
                <MenuItem value="Dr.">Dr.</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Name"
              value={newContact.name}
              onChange={handleChange("name")}
              autoFocus
              fullWidth
              size="small"
              sx={{ mb: 2 }}
              required
              disabled={saving}
            />
            <TextField
              label="Mobile"
              value={newContact.mobile}
              onChange={handleChange("mobile")}
              fullWidth
              size="small"
              sx={{ mb: 2 }}
              disabled={saving}
            />
            <TextField
              label="Email"
              value={newContact.email}
              onChange={handleChange("email")}
              fullWidth
              size="small"
              type="email"
              sx={{ mb: 2 }}
              disabled={saving}
            />
            <TextField
              label="Address"
              value={newContact.address}
              onChange={handleChange("address")}
              fullWidth
              size="small"
              sx={{ mb: 2 }}
              disabled={saving}
            />
            <TextField
              label="Area/Location"
              value={newContact.area_location}
              onChange={handleChange("area_location")}
              fullWidth
              size="small"
              sx={{ mb: 2 }}
              disabled={saving}
            />
            <TextField
              label="City"
              value={newContact.city}
              onChange={handleChange("city")}
              fullWidth
              size="small"
              sx={{ mb: 2 }}
              disabled={saving}
            />
            <TextField
              label="Postal Code"
              value={newContact.postal_code}
              onChange={handleChange("postal_code")}
              fullWidth
              size="small"
              sx={{ mb: 2 }}
              disabled={saving}
            />
            <TextField
              label="Department"
              value={newContact.department}
              onChange={handleChange("department")}
              fullWidth
              size="small"
              sx={{ mb: 2 }}
              disabled={saving}
            />
            <TextField
              label="Designation"
              value={newContact.designation}
              onChange={handleChange("designation")}
              fullWidth
              size="small"
              sx={{ mb: 2 }}
              disabled={saving}
            />
            {saveError && (
              <Box sx={{ color: "error.main", mt: 1 }}>{saveError}</Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleDialogSubmit}
            disabled={saving}
          >
            {saving ? "Saving..." : "Add"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}